"use client";
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from "@/components/ui/button";
import Slider from "@/components/ui/slider";
import { ModalCloseButton } from "@/components/ui/modal-close-button";
import { getCookiePreferences, setCookiePreferences as setCookiePrefs } from '@/utils/cookieManager';

const CookieConsent = () => {
  const t = useTranslations('cookie');

  const [showBubble, setShowBubble] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate client-only mount read of localStorage to avoid SSR hydration mismatch
    setIsMounted(true);

    let hasConsented = null;
    try {
      hasConsented = localStorage.getItem('cookie-consent');
      const storedPreferences = getCookiePreferences();
      if (storedPreferences) {
        setCookiePreferences(prev => ({
          ...prev,
          ...storedPreferences,
          necessary: true,
          functional: true,
        }));
      }
    } catch {
      // localStorage not available - handle silently
    }
    if (!hasConsented) {
      setShowPopup(true);
      setShowBubble(true);
    }
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    // Analytics loading is handled by AnalyticsManager via events
    // Functional cleanup intentionally omitted to avoid removing required app state
  }, [cookiePreferences, isMounted]);

  useEffect(() => {
    const syncPreferencesFromStorage = (event: StorageEvent) => {
      if (event.key === 'cookie-preferences') {
        const storedPreferences = getCookiePreferences();
        if (storedPreferences) {
          setCookiePreferences(prev => ({
            ...prev,
            ...storedPreferences,
            necessary: true,
            functional: true,
          }));
        }
      }
    };

    window.addEventListener('storage', syncPreferencesFromStorage);

    // Also listen for programmatic preference changes (same-tab)
    const onPrefs = (e: Event) => {
      try {
        const detail = (e as CustomEvent)?.detail as typeof cookiePreferences | undefined;
        if (detail) {
          setCookiePreferences(prev => ({
            ...prev,
            ...detail,
            necessary: true,
            functional: true,
          }));
        } else {
          const stored = getCookiePreferences();
          if (stored) {
            setCookiePreferences(prev => ({
              ...prev,
              ...stored,
              necessary: true,
              functional: true,
            }));
          }
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener('cookie-preferences-changed', onPrefs as EventListener);

    return () => {
      window.removeEventListener('storage', syncPreferencesFromStorage);
      window.removeEventListener('cookie-preferences-changed', onPrefs as EventListener);
    };
  }, []);

  // Lock body scroll when popup is open
  // Do not lock the body when the panel is open - allow scrolling and interaction
  // (previous implementation blocked scroll and prevented interacting with page)

  const handleAcceptAll = () => {
    const preferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setCookiePreferences(preferences);
    try {
      setCookiePrefs(preferences);
    } catch {
      // ignore
    }
    try {
      localStorage.setItem('cookie-consent', 'accepted');
    } catch {
      // ignore
    }
    setShowBubble(false);
    setShowPopup(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const preferences = {
      necessary: true,
      functional: true,
      analytics: false,
      marketing: false,
    };
    setCookiePreferences(preferences);
    try {
      setCookiePrefs(preferences);
    } catch {
      // ignore
    }
    try {
      localStorage.setItem('cookie-consent', 'rejected');
    } catch {
      // ignore
    }
    setShowBubble(false);
    setShowPopup(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    const normalizedPreferences = {
      ...cookiePreferences,
      necessary: true,
      functional: true,
    };
    try {
      localStorage.setItem('cookie-consent', 'customized');
    } catch {
      // localStorage not available - handle silently
    }

    // Centralized write (also emits events)
    try {
      setCookiePrefs(normalizedPreferences);
    } catch {
      try {
        localStorage.setItem('cookie-preferences', JSON.stringify(normalizedPreferences));
        window.dispatchEvent(new CustomEvent('cookie-preferences-changed', { detail: normalizedPreferences }));
      } catch {
        // ignore
      }
    }

    setShowBubble(false);
    setShowPopup(false);
    setShowSettings(false);
  };

  // Allow other pages to open the cookie popup (e.g., Legal page)
  useEffect(() => {
    const onOpen = (e: Event) => {
      try {
        // support opening with detail: { settings: true }
        const detail = (e as CustomEvent)?.detail as { settings?: boolean } | undefined;
        setShowPopup(true);
        if (detail?.settings) setShowSettings(true);
      } catch {
        setShowPopup(true);
      }
    };

    window.addEventListener('open-cookie-popup', onOpen as EventListener);
    return () => window.removeEventListener('open-cookie-popup', onOpen as EventListener);
  }, []);

  const handlePreferenceChange = (type: 'necessary' | 'functional' | 'analytics' | 'marketing') => {
    if (type === 'necessary' || type === 'functional') return;
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type as keyof typeof prev]
    }));
  };

  const openPopup = () => setShowPopup(true);
  const closePopup = () => {
    setShowPopup(false);
    setShowSettings(false);
  };

  if (!showBubble && !showPopup) return null;
  if (!isMounted) return null;

  const modalContent = showPopup ? (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <div className="w-[86vw] max-w-md pointer-events-auto bg-card text-card-foreground rounded-2xl shadow-2xl border border-border transition-transform transform-gpu fixed right-3 bottom-3 sm:right-6 sm:bottom-6 max-h-[90svh] overflow-hidden">
        <div className="flex max-h-[90svh] flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-3 sm:px-4 pt-1 pb-3">
            <div className="pt-1 pb-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-2 mt-1">
                    <Cookie className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-foreground">{t('title')}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 self-start sm:self-auto">
                  <ModalCloseButton onClick={closePopup} variant="light" size="sm" />
                </div>
              </div>
              {!showSettings && (
                <p className="text-sm text-muted-foreground mt-2">{t('description')}</p>
              )}
            </div>

            {!showSettings ? (
              <div className="mt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                  <Button variant="ghost" onClick={handleAcceptAll} className="flex-1 py-2 text-sm px-3 bg-slate-900 text-white font-semibold hover:bg-slate-800">{t('acceptAll')}</Button>
                  <Button variant="ghost" onClick={handleRejectAll} className="flex-1 py-2 text-sm px-3 bg-slate-100 text-slate-900 border border-slate-200 hover:bg-slate-200">{t('rejectAll')}</Button>
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className="w-full text-center text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
                  >
                    {t('customize')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{t('personalizeTitle')}</h4>
                  <p className="text-xs text-muted-foreground">{t('personalizeDescription')}</p>
                </div>

                {[
                  {
                    key: 'essentialFunctional',
                    title: `${t('necessary')}`,
                    description: `${t('necessaryDescription')} ${t('functionalDescription')}`,
                    icon: <Shield className="text-brand-red dark:text-brand-red-soft" size={18} />,
                    enabled: true,
                    toggle: false,
                  },
                  {
                    key: 'analytics',
                    title: t('analytics'),
                    description: t('analyticsDescription'),
                    icon: <BarChart3 className="text-[hsl(var(--color-dark-blue))]" size={18} />,
                    enabled: cookiePreferences.analytics,
                    toggle: true,
                  },
                  {
                    key: 'marketing',
                    title: t('marketing'),
                    description: t('marketingDescription'),
                    icon: <Megaphone className="text-pink-500" size={18} />,
                    enabled: cookiePreferences.marketing,
                    toggle: true,
                  },
                ].map(({ key, icon, enabled, toggle, title, description }) => (
                  <div key={key} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 bg-card border border-border rounded-lg">
                    <div className="flex items-center gap-3">
                      {icon}
                      <div>
                        <div className="font-medium text-foreground">{title}</div>
                        <div className="text-xs text-muted-foreground">{description}</div>
                      </div>
                    </div>
                    <div className="self-end sm:self-auto">
                      {toggle ? (
                        <Slider checked={enabled} onChange={() => handlePreferenceChange(key as 'necessary' | 'functional' | 'analytics' | 'marketing')} />
                      ) : (
                        <Slider checked={true} onChange={() => {}} className="pointer-events-none opacity-70" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 text-xs text-muted-foreground">
              {t('footer')}{' '}
              <Link href="/legal?tab=privacy" className="text-blue hover:underline" target="_blank" rel="noopener noreferrer">{t('privacy')}</Link>{' '}
              {t('and')}{' '}
              <Link href="/legal?tab=cookies" className="text-blue hover:underline" target="_blank" rel="noopener noreferrer">{t('cookies')}</Link>
            </div>
          </div>

          {showSettings && (
            <div className="border-t border-border bg-card/95 backdrop-blur px-3 sm:px-4 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button variant="ghost" onClick={() => setShowSettings(false)} className="flex-1 py-2 text-sm px-3 bg-slate-100 text-slate-900 border border-slate-200 hover:bg-slate-200">{t('back')}</Button>
                <Button variant="ghost" onClick={handleSavePreferences} className="flex-1 py-2 text-sm px-3 bg-slate-900 text-white hover:bg-slate-800">{t('save')}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Cookie Bubble */}
      {showBubble && !showPopup && (
        <div className="fixed bottom-6 left-6 z-[9998]">
          <button
            onClick={openPopup}
            className="bg-gradient-to-r from-amber-400 to-orange-500 text-black rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 ring-4 ring-amber-400/20"
            aria-label={t('openCookieSettings')}
          >
            <Cookie size={24} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Cookie Popup - Rendered in portal */}
      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
};

export default CookieConsent;
